import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Image,
  Row,
  Table,
  OverlayTrigger,
  Popover,
} from "react-bootstrap";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import * as constants from "../../constants/CONSTANT";

function extractPlaceholders(text) {
  // Returns [1,2,3] for text with {{1}}, {{2}}, {{3}}
  const matches = text.match(/{{(\d+)}}/g) || [];
  const nums = matches.map((m) => parseInt(m.replace(/[{}]/g, ""), 10));
  return Array.from(new Set(nums)).sort((a, b) => a - b);
}

function validateAndFixExamples(text, examples) {
  const placeholders = extractPlaceholders(text);
  let fixedExamples = Array(placeholders.length).fill("");
  if (Array.isArray(examples)) {
    for (let i = 0; i < Math.min(placeholders.length, examples.length); i++) {
      fixedExamples[i] = examples[i] || "";
    }
  }
  return fixedExamples;
}

function formatTemplateName(name) {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

const sample_video = `${constants.FRONTEND_URL}/user_images/sample_video.mp4`;
const sample_image = `${constants.FRONTEND_URL}/user_images/placeholder.jpg`;
// console.log("sample_image",sample_image)
// Defensive initial card with all fields as arrays
const initialCard = {
  header_type: "image", // 'image' or 'video'
  header_file: null, // File or URL
  header_handle: "", // WhatsApp asset handle
  preview_url: "", // for local preview only
  body_text: "",
  body_text_example: [],
  buttons: [], // { type, text, url, phone_number, example }
};

const CarouselTemplate = ({ selectedWhatsAppSetting, previewData }) => {
  const [templateName, setTemplateName] = useState("");
  const [language, setLanguage] = useState("");
  const [mainBody, setMainBody] = useState("");
  const [mainBodyExamples, setMainBodyExamples] = useState([]);
  const [cards, setCards] = useState([{ ...initialCard }]);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState("");
  const [isSending, setIsSending] = useState(false);
  const mainBodyRef = useRef(null);
  const navigate = useNavigate();
  const VAR_REGEX = /{{\d+}}/g;
  const isEditMode = !!(
    previewData &&
    (previewData.id || previewData.template_id)
  );

  // Prefill state from previewData if present (edit/view mode)
  useEffect(() => {
    if (previewData && Object.keys(previewData).length > 0) {
      //console.log("object", previewData);
      setTemplateName(previewData.template_name || previewData.name || "");
      setLanguage(previewData.language || "");
      setMainBody(
        previewData.message_body ||
          (previewData.components && previewData.components[0]?.text) ||
          "",
      );
      // Main body examples
      let mainExamples = [];
      if (previewData.example_body_text) {
        try {
          const parsed =
            typeof previewData.example_body_text === "string"
              ? JSON.parse(previewData.example_body_text)
              : previewData.example_body_text;
          if (Array.isArray(parsed) && parsed.length > 0)
            mainExamples = Array.isArray(parsed[0]) ? parsed[0] : parsed;
        } catch {}
      } else if (
        previewData.components &&
        previewData.components[0]?.example?.body_text
      ) {
        mainExamples = previewData.components[0].example.body_text[0] || [];
      }
      setMainBodyExamples(mainExamples);
      // Cards (robust mapping for edit mode)
      if (previewData.cards && Array.isArray(previewData.cards)) {
        const mappedCards = previewData.cards.map((card) => ({
          header_type: (card.header_type || "image").toLowerCase(),
          header_image_url: card.header_image_url || "",
          header_video_url: card.header_video_url || "",
          body_text: card.body_text || "",
          body_text_example: card.body_text_example || [],
          buttons: (card.buttons || []).map((btn) => ({
            type: (btn.type || "quick_reply").toLowerCase(),
            text: btn.text || "",
            url: btn.url || "",
            phone_number: btn.phone_number || "",
            example: btn.example || "",
          })),
          header_file: null,
          preview_url:
            card.header_image_url &&
            (card.header_type || "image").toLowerCase() === "image"
              ? card.header_image_url
              : card.header_video_url &&
                  (card.header_type || "video").toLowerCase() === "video"
                ? card.header_video_url
                : "",
        }));
        setCards(mappedCards);
        //console.log("Edit mode cards:", mappedCards);
      }
    } else {
      // If not editing, ensure all fields are blank
      setTemplateName("");
      setLanguage("");
      setMainBody("");
      setMainBodyExamples([]);
      setCards([{ ...initialCard, body_text_example: [], buttons: [] }]);
      setFieldErrors({});
      setPayload(null);
      setError("");
    }
  }, [previewData]);

  // 1. Fix card state initialization in edit mode
  useEffect(() => {
    if (previewData && previewData.cards && Array.isArray(previewData.cards)) {
      const mappedCards = previewData.cards.map((card) => ({
        header_type: (card.header_type || "image").toLowerCase(),
        header_image_url: card.header_image_url || "",
        header_video_url: card.header_video_url || "",
        body_text: card.body_text || "",
        body_text_example: card.body_text_example || [],
        buttons: (card.buttons || []).map((btn) => ({
          type: (btn.type || "quick_reply").toLowerCase(),
          text: btn.text || "",
          url: btn.url || "",
          phone_number: btn.phone_number || "",
          example: btn.example || "",
        })),
        header_file: null,
        preview_url:
          card.header_image_url &&
          (card.header_type || "image").toLowerCase() === "image"
            ? card.header_image_url
            : card.header_video_url &&
                (card.header_type || "video").toLowerCase() === "video"
              ? card.header_video_url
              : "",
      }));
      setCards(mappedCards);
      //console.log("Edit mode cards:", mappedCards);
    }
  }, [previewData]);

  // --- Main Body Variable Handling ---
  const addMainBodyVariable = () => {
    const nextVar = (mainBody.match(/{{\d+}}/g) || []).length + 1;
    setMainBody(mainBody + ` {{${nextVar}}}`);
    setMainBodyExamples([...mainBodyExamples, ""]);
  };

  // --- Validation helpers ---
  const validateTemplate = () => {
    let errors = {};
    // Template name
    if (!templateName.trim())
      errors.templateName = "Template name is required.";
    if (templateName.length > 512) errors.templateName = "Max 512 characters.";
    // Language
    if (!language) errors.language = "Language is required.";
    // Main message body
    if (!mainBody.trim()) errors.mainBody = "Message body is required.";
    if (mainBody.length > 1024) errors.mainBody = "Max 1024 characters.";
    // Main body variables
    const mainPlaceholders = (mainBody || "").match(/{{\d+}}/g) || [];
    if (mainPlaceholders.length > 0) {
      if (
        !Array.isArray(mainBodyExamples) ||
        mainBodyExamples.length !== mainPlaceholders.length
      )
        errors.mainBodyExamples = `Provide example for each variable in message body (${mainPlaceholders.length} required).`;
      if (mainBodyExamples.some((v) => !v || !v.trim()))
        errors.mainBodyExamples =
          "No example for message body variable can be empty.";
    }
    // Cards
    if (!Array.isArray(cards) || cards.length === 0)
      errors.cards = "At least 1 card required.";
    if (cards.length > 10) errors.cards = "Max 10 cards allowed.";
    // All cards must have the same button types and order
    let buttonTypes = null;
    cards.forEach((card, idx) => {
      // Header
      if (!card.header_type)
        errors[`card_header_type_${idx}`] = "Header type required.";
      // if (!card.header_file && !card.header_handle)
      //   errors[`card_header_file_${idx}`] = "Image/Video required.";
      // Card body
      if (!card.body_text || !card.body_text.trim())
        errors[`card_body_text_${idx}`] = "Card body required.";
      if ((card.body_text || "").length > 160)
        errors[`card_body_text_${idx}`] = "Max 160 characters.";
      // Card body variables
      const cardPlaceholders = (card.body_text || "").match(/{{\d+}}/g) || [];
      if (cardPlaceholders.length > 0) {
        if (
          !Array.isArray(card.body_text_example) ||
          card.body_text_example.length !== cardPlaceholders.length
        )
          errors[`card_body_text_example_${idx}`] =
            `Provide example for each variable in card body (${cardPlaceholders.length} required).`;
        if (card.body_text_example.some((v) => !v || !v.trim()))
          errors[`card_body_text_example_${idx}`] =
            "No example for card body variable can be empty.";
      }
      // Buttons
      if (!Array.isArray(card.buttons) || card.buttons.length < 1)
        errors[`card_buttons_${idx}`] = "At least 1 button required.";
      if (card.buttons.length > 2)
        errors[`card_buttons_${idx}`] = "Max 2 buttons allowed.";
      // All cards must have the same button types and order
      const types = card.buttons.map((b) => b.type);
      if (buttonTypes === null) buttonTypes = types;
      else if (
        types.length !== buttonTypes.length ||
        types.some((t, i) => t !== buttonTypes[i])
      )
        errors[`card_buttons_${idx}`] =
          "All cards must have the same button types and order.";
      // Button fields
      card.buttons.forEach((btn, bidx) => {
        if (!btn.text || !btn.text.trim())
          errors[`card_btn_text_${idx}_${bidx}`] = "Button label required.";
        if ((btn.text || "").length > 25)
          errors[`card_btn_text_${idx}_${bidx}`] = "Max 25 characters.";
        if (btn.type === "url") {
          if (!btn.url || !btn.url.trim())
            errors[`card_btn_url_${idx}_${bidx}`] = "URL required.";
          if ((btn.url || "").length > 2000)
            errors[`card_btn_url_${idx}_${bidx}`] = "Max 2000 characters.";
          // If URL contains a variable, example is required
          if ((btn.url || "").includes("{{1}}")) {
            if (!btn.example || !btn.example.trim())
              errors[`card_btn_url_example_${idx}_${bidx}`] =
                "URL variable example required.";
            if ((btn.example || "").length > 2000)
              errors[`card_btn_url_example_${idx}_${bidx}`] =
                "Max 2000 characters for URL example.";
          }
        }
        if (btn.type === "phone_number") {
          if (!btn.phone_number || !btn.phone_number.trim())
            errors[`card_btn_phone_${idx}_${bidx}`] = "Phone number required.";
          if ((btn.phone_number || "").length > 20)
            errors[`card_btn_phone_${idx}_${bidx}`] = "Max 20 characters.";
        }
      });
    });
    return errors;
  };

  const [fieldErrors, setFieldErrors] = useState({});

  // --- Submit Handler ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSending(true);
    const errors = validateTemplate();
    //console.log(" validateTemplate errors:", errors);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setIsSending(false);
      toast.error("Please fix the errors in the form.");
      return;
    }
    try {
      if (!Array.isArray(cards) || cards.length === 0) {
        toast.error("At least one card is required.");
        return;
      }
      if (cards.length > 10) {
        toast.error("A maximum of 10 cards is allowed.");
        return;
      }
      for (let i = 0; i < cards.length; i++) {
        const card = cards[i];
        if (
          !card.header_type ||
          (card.header_type !== "image" && card.header_type !== "video")
        ) {
          toast.error(`Card ${i + 1}: Header type must be 'Image' or 'Video'.`);
          return;
        }
        // if (
        //   card.header_type === "image" &&
        //   !card.header_file &&
        //   !card.header_handle
        // ) {
        //   toast.error(
        //     `Card ${i + 1}: Image is required for header type 'Image'.`
        //   );
        //   return;
        // }
        // if (
        //   card.header_type === "video" &&
        //   !card.header_file &&
        //   !card.header_handle
        // ) {
        //   toast.error(
        //     `Card ${i + 1}: Video is required for header type 'Video'.`
        //   );
        //   return;
        // }
        if (!card.body_text || !card.body_text.trim()) {
          toast.error(`Card ${i + 1}: Card body is required.`);
          return;
        }
        if (!Array.isArray(card.buttons) || card.buttons.length === 0) {
          toast.error(`Card ${i + 1}: At least one button is required.`);
          return;
        }
        // Add more per-card field checks as needed
      }
      // --- VALIDATION ---
      // Main body
      const mainPlaceholders = extractPlaceholders(mainBody);
      const mainExamples = validateAndFixExamples(mainBody, mainBodyExamples);
      if (
        mainPlaceholders.length !== mainExamples.length ||
        mainExamples.some((v) => !v.trim())
      ) {
        toast.error(
          "Main message body: Number of example values must match the number of variables, and no example can be empty.",
        );
        setIsSending(false);
        return;
      }
      // Each card
      for (let i = 0; i < cards.length; i++) {
        const card = cards[i];
        const cardPlaceholders = extractPlaceholders(card.body_text);
        const cardExamples = validateAndFixExamples(
          card.body_text,
          card.body_text_example,
        );
        if (
          cardPlaceholders.length !== cardExamples.length ||
          cardExamples.some((v) => !v.trim())
        ) {
          toast.error(
            `Card ${
              i + 1
            }: Number of example values must match the number of variables, and no example can be empty.`,
          );
          setIsSending(false);
          return;
        }
      }
      // 1. Upload all card media and get handles
      const cardsWithHandles = await uploadAllCardMedia();
      // 2. Build WhatsApp API payload (with fixed examples)
      const formattedName = formatTemplateName(templateName);
      //console.log("🚀 ~ handleSubmit ~ formattedName:", formattedName);
      const waPayload = {
        name: formattedName,
        language,
        category: "marketing",
        components: [
          {
            type: "body",
            text: mainBody,
            example:
              mainPlaceholders.length > 0
                ? { body_text: [mainExamples] }
                : undefined,
          },
          {
            type: "carousel",
            cards: cardsWithHandles.map((card) => {
              const cardPlaceholders = extractPlaceholders(card.body_text);
              const cardExamples = validateAndFixExamples(
                card.body_text,
                card.body_text_example,
              );
              return {
                components: [
                  {
                    type: "header",
                    format: card.header_type,
                    example: {
                      header_handle: [card.header_handle],
                    },
                  },
                  {
                    type: "body",
                    text: card.body_text,
                    ...(cardPlaceholders.length > 0
                      ? { example: { body_text: [cardExamples] } }
                      : {}),
                  },
                  {
                    type: "buttons",
                    buttons: card.buttons.map((btn) => ({
                      type: btn.type,
                      text: btn.text,
                      ...(btn.type === "url"
                        ? {
                            url: btn.url,
                            example: btn.example ? [btn.example] : undefined,
                          }
                        : {}),
                      ...(btn.type === "phone_number"
                        ? { phone_number: btn.phone_number }
                        : {}),
                    })),
                  },
                ],
              };
            }),
          },
        ],
      };
      setPayload(waPayload);
      // 3. Send to WhatsApp API
      let waRes;
      // if (isEditMode) {
      //   waRes = await WhatsAppAPI.updateMarketingTemplate(
      //     previewData.id || previewData.template_id,
      //     waPayload,
      //     selectedWhatsAppSetting
      //   );
      // // console.log("🚀 ~ handleSubmit ~ waRes:", waRes);
      // } else {
      waRes = await WhatsAppAPI.createMarketingTemplate(
        waPayload,
        selectedWhatsAppSetting,
      );
      // }
      //console.log("🚀 ~ handleSubmit ~ waRes:", waRes);
      if (waRes?.error) {
        toast.error(waRes.error.body || "WhatsApp API error");
        setIsSending(false);
        return;
      }
      // 4. Build DB payload (with fixed examples)
      const dbPayload = {
        template_id: waRes?.id || "",
        template_name: formattedName,
        language,
        category: waRes?.category || "marketing",
        header: null,
        header_body: null,
        message_body: mainBody,
        example_body_text: JSON.stringify([mainExamples]),
        footer: null,
        buttons: null,
        cards: cardsWithHandles.map((card) => {
          const cardExamples = validateAndFixExamples(
            card.body_text,
            card.body_text_example,
          );
          return {
            header: { format: card.header_type, handle: card.header_handle },
            body: { text: card.body_text, example: cardExamples },
            buttons: card.buttons,
          };
        }),
        template_type: "carousel",
        business_number: selectedWhatsAppSetting,
      };
      //console.log("🚀 ~ handleSubmit ~ dbPayload:", dbPayload);
      // 5. Save to DB
      let dbRes;
      // if (isEditMode) {
      //   dbRes = await WhatsAppAPI.updateMessageTemplateData(
      //     previewData.id || previewData.template_id,
      //     dbPayload
      //   );
      // } else {
      dbRes = await WhatsAppAPI.createMessageTemplateData(dbPayload);
      // }
      if (dbRes?.error) {
        toast.error(dbRes.error.message || "DB Save error");
        setIsSending(false);
        return;
      }
      toast.success(
        isEditMode
          ? "Carousel template updated successfully!"
          : "Carousel template created successfully!",
      );
      setTimeout(() => navigate("/whatsapp_template"), 1200);
    } catch (err) {
      toast.error("Unexpected error: " + (err.message || err));
    } finally {
      setIsSending(false);
    }
  };

  // const uploadAllCardMedia = async () => {
  //   const cardsWithHandles = [];
  //   for (let i = 0; i < cards.length; i++) {
  //     const card = cards[i];
  //     let headerHandle = card.header_handle;
  //     if (!headerHandle && card.header_image_url) {
  //       // If we have an image URL, treat it as already uploaded
  //       headerHandle = card.header_image_url;
  //     }
  //     // Only upload if we have a file and no handle yet
  //     if (!headerHandle && card.header_file) {
  //       try {
  //         const sessionId = await WhatsAppAPI.generateSessionId(
  //           card.header_file,
  //           selectedWhatsAppSetting
  //         );
  //         if (sessionId.id) {
  //           const document = await WhatsAppAPI.uploadDocumentSessionId(
  //             card.header_file,
  //             sessionId.id,
  //             selectedWhatsAppSetting
  //           );
  //           headerHandle = document.h;
  //         } else {
  //           toast.error(
  //             `Card ${i + 1}: Failed to get sessionId for media upload.`
  //           );
  //           return [];
  //         }
  //       } catch (err) {
  //         toast.error(`Card ${i + 1}: Failed to upload media: ${err.message}`);
  //         return [];
  //       }
  //     }
  //     cardsWithHandles.push({
  //       ...card,
  //       header_handle: headerHandle,
  //       header_file: null, // Don't send file object to API
  //     });
  //   }
  //   return cardsWithHandles;
  // };
  const uploadAllCardMedia = async () => {
    let imageHandle = null;
    let videoHandle = null;

    const uploadSample = async (type) => {
      const sampleUrl =
        type === "video"
          ? "/user_images/sample_video.mp4"
          : "/user_images/placeholder.jpg";

      try {
        //console.log(`📦 Fetching ${type.toUpperCase()} sample from:`, sampleUrl);
        const response = await fetch(sampleUrl);
        const blob = await response.blob();

        if (!blob || blob.size === 0) {
          throw new Error(`Blob fetch failed or empty for ${type}`);
        }

        const file = new File([blob], sampleUrl.split("/").pop(), {
          type: type === "video" ? "video/mp4" : "image/jpeg",
        });

        //console.log(`🎬 Created File object for ${type}:`, file);

        const sessionId = await WhatsAppAPI.generateSessionId(
          file,
          selectedWhatsAppSetting,
        );
        //console.log(`🔐 Session ID for ${type}:`, sessionId);

        if (!sessionId?.id) {
          throw new Error(`Session ID not returned for ${type}`);
        }

        const document = await WhatsAppAPI.uploadDocumentSessionId(
          file,
          sessionId.id,
          selectedWhatsAppSetting,
        );
        //console.log(`📤 Upload response for ${type}:`, document);

        if (!document || !document.h) {
          throw new Error(`WhatsApp handle not returned for ${type}`);
        }

        return document.h;
      } catch (err) {
        console.error(`❌ Upload failed for ${type}:`, err);
        toast.error(`${type} sample upload failed: ${err.message}`);
        return null;
      }
    };

    // Determine if image/video is required
    const needsImage = cards.some(
      (card) => card.header_type?.toLowerCase() === "image",
    );
    const needsVideo = cards.some(
      (card) => card.header_type?.toLowerCase() === "video",
    );

    if (needsImage) {
      imageHandle = await uploadSample("image");
      if (!imageHandle) {
        console.warn("⚠️ Image upload failed. Continuing without handle.");
      }
    }

    if (needsVideo) {
      videoHandle = await uploadSample("video");
      if (!videoHandle) {
        console.warn("⚠️ Video upload failed. Continuing without handle.");
      }
    }

    // Assign handles and preview URLs to each card
    const cardsWithHandles = cards.map((card, idx) => {
      const type = card.header_type?.toLowerCase();
      const handle = type === "video" ? videoHandle : imageHandle;
      const previewURL =
        type === "video"
          ? `${constants.FRONTEND_URL}/user_images/sample_video.mp4`
          : `${constants.FRONTEND_URL}/user_images/placeholder.jpg`;

      //console.log(`📇 Card #${idx + 1} - Type: ${type}, Handle: ${handle}`);

      return {
        ...card,
        header_handle: handle,
        header_file: null,
        preview_url: handle ? previewURL : null,
      };
    });

    //console.log("✅ Cards after media assignment:", cardsWithHandles);
    return cardsWithHandles;
  };

  const handleMainBodyExampleChange = (idx, value) => {
    const newExamples = [...mainBodyExamples];
    newExamples[idx] = value;
    setMainBodyExamples(newExamples);
  };

  const handleCardChange = (cardIdx, field, value) => {
    setCards((prev) => {
      const updated = [...prev];
      const card = { ...updated[cardIdx] };

      card[field] = value;

      if (field === "body_text") {
        card.body_text_example = syncPlaceholders(
          value,
          card.body_text_example || [],
        );
      }

      updated[cardIdx] = card;
      return updated;
    });
  };

  // // Defensive handleCardFileUpload: always preserve/initialize arrays
  // const handleCardFileUpload = async (idx, file) => {
  //   if (!file) {
  //     setFieldErrors({ [`card_header_file_${idx}`]: "Image/Video required." });
  //     return;
  //   }

  //   const reader = new FileReader();
  //   reader.onloadend = () => {
  //     const url = reader.result;
  //     setCards((prevCards) => {
  //       const newCards = Array.isArray(prevCards)
  //         ? [...prevCards]
  //         : [{ ...initialCard, body_text_example: [], buttons: [] }];
  //       newCards[idx].preview_url = url;
  //       newCards[idx].header_file = file; // Store file object for potential re-upload
  //       if (!Array.isArray(newCards[idx].body_text_example))
  //         newCards[idx].body_text_example = [];
  //       if (!Array.isArray(newCards[idx].buttons)) newCards[idx].buttons = [];
  //       setFieldErrors((prev) => ({
  //         ...prev,
  //         [`card_header_file_${idx}`]: undefined,
  //       }));
  //       return newCards;
  //     });
  //   };
  //   reader.readAsDataURL(file);
  // };

  const addCardBodyVariable = (cardIdx) => {
    const card = cards[cardIdx];
    const nextVar = ((card.body_text || "").match(/{{\d+}}/g) || []).length + 1;
    const newBodyText = card.body_text + ` {{${nextVar}}}`;
    const newBodyExamples = [...card.body_text_example, ""];

    setCards((prevCards) => {
      const newCards = Array.isArray(prevCards)
        ? [...prevCards]
        : [{ ...initialCard, body_text_example: [], buttons: [] }];
      newCards[cardIdx].body_text = newBodyText;
      newCards[cardIdx].body_text_example = newBodyExamples;
      return newCards;
    });
  };

  // Defensive handleCardBodyExampleChange
  const handleCardBodyExampleChange = (cardIdx, exIdx, value) => {
    setCards((prevCards) => {
      const newCards = Array.isArray(prevCards)
        ? [...prevCards]
        : [{ ...initialCard, body_text_example: [], buttons: [] }];
      if (!Array.isArray(newCards[cardIdx].body_text_example))
        newCards[cardIdx].body_text_example = [];
      newCards[cardIdx].body_text_example[exIdx] = value;
      return newCards;
    });
  };

  // Defensive handleCardButtonChange
  const handleCardButtonChange = (cardIdx, btnIdx, field, value) => {
    setCards((prevCards) => {
      const newCards = Array.isArray(prevCards)
        ? [...prevCards]
        : [{ ...initialCard, body_text_example: [], buttons: [] }];
      if (!Array.isArray(newCards[cardIdx].buttons))
        newCards[cardIdx].buttons = [];
      if (!newCards[cardIdx].buttons[btnIdx])
        newCards[cardIdx].buttons[btnIdx] = {
          type: "quick_reply",
          text: "",
          url: "",
          phone_number: "",
          example: "",
        };
      newCards[cardIdx].buttons[btnIdx][field] = value;
      return newCards;
    });
  };

  // Defensive removeCardButton
  const removeCardButton = (cardIdx, btnIdx) => {
    setCards((prevCards) => {
      const newCards = Array.isArray(prevCards)
        ? [...prevCards]
        : [{ ...initialCard, body_text_example: [], buttons: [] }];
      if (!Array.isArray(newCards[cardIdx].buttons))
        newCards[cardIdx].buttons = [];
      newCards[cardIdx].buttons.splice(btnIdx, 1);
      return newCards;
    });
  };

  // Defensive addCardButton: always initialize all button fields
  const addCardButton = (cardIdx) => {
    setCards((prevCards) => {
      const newCards = Array.isArray(prevCards)
        ? [...prevCards]
        : [{ ...initialCard, body_text_example: [], buttons: [] }];
      if (!Array.isArray(newCards[cardIdx].buttons))
        newCards[cardIdx].buttons = [];
      newCards[cardIdx].buttons.push({
        type: "quick_reply",
        text: "",
        url: "",
        phone_number: "",
        example: "",
      });
      return newCards;
    });
  };

  const removeCard = (idx) => {
    setCards((prevCards) => prevCards.filter((_, i) => i !== idx));
  };

  // Defensive addCard: always initialize all fields
  const addCard = () => {
    setCards((prevCards) =>
      Array.isArray(prevCards)
        ? [...prevCards, { ...initialCard, body_text_example: [], buttons: [] }]
        : [{ ...initialCard, body_text_example: [], buttons: [] }],
    );
  };

  const handleRemoveMainBodyVariable = (varIdx) => {
    setMainBodyExamples((prev) => {
      const updated = [...prev];
      updated.splice(varIdx, 1); // remove corresponding example
      return updated;
    });

    setMainBody((prev) => {
      let placeholders = prev.match(/{{\d+}}/g) || [];
      if (varIdx < 0 || varIdx >= placeholders.length) return prev;

      let i = 0;
      const updated = prev.replace(/{{\d+}}/g, (match) => {
        if (i === varIdx) {
          i++;
          return ""; // remove the target placeholder
        }
        const newVal = `{{${i + (i > varIdx ? 0 : 1)}}}`;
        i++;
        return newVal;
      });

      return updated.replace(/\s+/g, " ").trim(); // clean spaces
    });
  };

  const handleRemoveCardBodyVariable = (cardIdx, varIdx) => {
    setCards((prevCards) => {
      const updatedCards = [...prevCards];
      const card = { ...updatedCards[cardIdx] };

      // Remove example
      if (Array.isArray(card.body_text_example)) {
        const updatedExamples = [...card.body_text_example];
        updatedExamples.splice(varIdx, 1);
        card.body_text_example = updatedExamples;
      }

      let prevText = card.body_text || "";
      const regex = VAR_REGEX;
      let match;
      let i = 0;
      let start = -1;
      let end = -1;

      // Find {{n}} position
      while ((match = regex.exec(prevText)) !== null) {
        if (i === varIdx) {
          start = match.index;
          end = match.index + match[0].length;
          break;
        }
        i++;
      }

      if (start !== -1) {
        let before = prevText.slice(0, start);
        let after = prevText.slice(end);

        if (before.endsWith(" ") && after.startsWith(" ")) {
          before = before.slice(0, -1);
        }

        if (before.endsWith("\n") && after.startsWith("\n")) {
          after = after.slice(1);
        }

        card.body_text = (before + after).trim();
      }

      // 🔥 Reindex placeholders
      let placeholders = card.body_text.match(/{{\d+}}/g) || [];
      placeholders.forEach((_, i) => {
        card.body_text = card.body_text.replace(/{{\d+}}/, `{{${i + 1}}}`);
      });

      updatedCards[cardIdx] = card;
      return updatedCards;
    });
  };

  const syncPlaceholders = (text, examples) => {
    const placeholders = text.match(/{{\d+}}/g) || [];
    let synced = [...examples];

    // Trim if too many
    if (synced.length > placeholders.length) {
      synced = synced.slice(0, placeholders.length);
    }
    // Pad if too few
    if (synced.length < placeholders.length) {
      synced = [
        ...synced,
        ...Array(placeholders.length - synced.length).fill(""),
      ];
    }

    return synced;
  };

  return (
    <>
      {!isSending ? (
        <Container className="mt-1">
          <Row className="mx-5 mb-1">
            <Card className="p-4 header-bg shadow-sm border-0 rounded-3">
              <h5 className="mb-2 fw-bold">💡 Quick Guide</h5>
              <ul className="mb-0 ps-3">
                <li>Max 10 cards allowed per carousel.</li>
                <li>Each card body max 160 characters.</li>
                <li>Only one newline allowed in card body.</li>
                <li>
                  If any card has buttons, then all cards must have buttons in
                  the same order and number.
                </li>
              </ul>
            </Card>
          </Row>

          <Row className="mx-5 g-0">
            <Col lg={12} sm={12} xs={12}>
              <Card className="h-100" style={{ border: "none" }}>
                <Card.Body>
                  <Row>
                    <Col lg={12} sm={12} xs={12}>
                      <Form.Group className="mx-2 mb-3">
                        <Form.Label
                          className="form-view-label"
                          htmlFor="formBasicName"
                        >
                          <b>Template name and language</b>
                        </Form.Label>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col lg={6} sm={12} xs={12}>
                        <Form.Group className="mx-2 mb-3">
                          <Form.Label>
                            Template Name{" "}
                            <span style={{ color: "red" }}>*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={templateName}
                            onChange={(e) => setTemplateName(e.target.value)}
                            placeholder="Enter template name"
                            isInvalid={!!fieldErrors.templateName}
                            isValid={
                              !fieldErrors.templateName &&
                              templateName.length > 0
                            }
                            required
                            style={{ height: "36px" }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {fieldErrors.templateName}
                          </Form.Control.Feedback>
                          <Form.Text className="text-muted"></Form.Text>
                        </Form.Group>
                      </Col>
                      <Col lg={6} sm={12} xs={12}>
                        <Form.Group className="mb-2 mx-3">
                          <Form.Label>Language</Form.Label>
                          <Form.Select
                            value={language}
                            onChange={(e) => setLanguage(e.target.value)}
                            isInvalid={!!fieldErrors.language}
                            isValid={!fieldErrors.language && language}
                            required
                            style={{ height: "36px" }}
                          >
                            <option value="">Select Language</option>
                            <option value="en">English</option>
                            <option value="en_US">English (US)</option>
                            <option value="en_GB">English (UK)</option>
                            <option value="hi">Hindi</option>
                            <option value="ur">Urdu</option>
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {fieldErrors.language}
                          </Form.Control.Feedback>
                          <Form.Text className="text-muted"></Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                    {/* <Row>
                  <Col lg={12}>
                    <Form.Group className="mb-3 mx-2">
                      <Form.Label>Message Body</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        ref={mainBodyRef}
                        value={mainBody}
                        onChange={(e) => setMainBody(e.target.value)}
                        placeholder="Enter main message body (supports variables)"
                        isInvalid={!!fieldErrors.mainBody}
                        isValid={!fieldErrors.mainBody && mainBody.length > 0}
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        {fieldErrors.mainBody}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                      </Form.Text>
                      <Col className="text-end">
                        <Button
                          className="mt-2"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            addMainBodyVariable();
                          }}
                        >
                          Add Variable
                        </Button>
                      </Col>
                    </Form.Group>
                    {(mainBody.match(/{{\d+}}/g) || []).length > 0 && (
                      <Table className="mx-2">
                        <thead>
                          <tr>
                            <th className='w-25'>Variable</th>
                            <th className='w-35'>Value</th>
                            <th className='w-35'>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Array.from({
                            length: Array.isArray(mainBodyExamples)
                              ? ((mainBody || "").match(/{{\d+}}/g) || [])
                                .length
                              : 0,
                          }).map((_, idx) => (
                            <tr key={idx}>
                              <td>{`{{${idx + 1}}}`}</td>
                              <td>
                                <Form.Control
                                  style={{ height: "36px" }}
                                  type="text"
                                  value={
                                    Array.isArray(mainBodyExamples)
                                      ? mainBodyExamples[idx] || ""
                                      : ""
                                  }
                                  onChange={(e) =>
                                    handleMainBodyExampleChange(
                                      idx,
                                      e.target.value
                                    )
                                  }
                                  placeholder={`Example for {{${idx + 1}}}`}
                                  isInvalid={!!fieldErrors.mainBodyExamples}
                                  isValid={
                                    !fieldErrors.mainBodyExamples &&
                                    Array.isArray(mainBodyExamples) &&
                                    mainBodyExamples[idx] &&
                                    mainBodyExamples[idx].length > 0
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {fieldErrors.mainBodyExamples}
                                </Form.Control.Feedback>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Col>
                </Row> */}

                    <Row>
                      <Col lg={12}>
                        <Form.Group className="mb-3 mx-2">
                          <Form.Label>Message Body</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            ref={mainBodyRef}
                            value={mainBody}
                            onChange={(e) => {
                              const newText = e.target.value;
                              setMainBody(newText);
                              setMainBodyExamples((prev) =>
                                syncPlaceholders(newText, prev),
                              );
                            }}
                            placeholder="Enter main message body (supports variables)"
                            isInvalid={!!fieldErrors.mainBody}
                            isValid={
                              !fieldErrors.mainBody && mainBody.length > 0
                            }
                            required
                          />

                          <Form.Control.Feedback type="invalid">
                            {fieldErrors.mainBody}
                          </Form.Control.Feedback>

                          <Col className="text-end">
                            <Button
                              className="mt-2"
                              size="sm"
                              onClick={(e) => {
                                e.preventDefault();
                                addMainBodyVariable();
                              }}
                            >
                              Add Variable
                            </Button>
                          </Col>
                        </Form.Group>
                        {(mainBody.match(/{{\d+}}/g) || []).length > 0 && (
                          <Table className="mx-2">
                            <thead>
                              <tr>
                                <th className="w-25">Variable</th>
                                <th className="w-35">Value</th>
                                <th className="w-35">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Array.from({
                                length: Array.isArray(mainBodyExamples)
                                  ? ((mainBody || "").match(/{{\d+}}/g) || [])
                                      .length
                                  : 0,
                              }).map((_, idx) => (
                                <tr key={idx}>
                                  <td>{`{{${idx + 1}}}`}</td>
                                  <td>
                                    <Form.Control
                                      style={{ height: "36px" }}
                                      type="text"
                                      value={
                                        Array.isArray(mainBodyExamples)
                                          ? mainBodyExamples[idx] || ""
                                          : ""
                                      }
                                      onChange={(e) =>
                                        handleMainBodyExampleChange(
                                          idx,
                                          e.target.value,
                                        )
                                      }
                                      placeholder={`Example for {{${idx + 1}}}`}
                                      isInvalid={!!fieldErrors.mainBodyExamples}
                                      isValid={
                                        !fieldErrors.mainBodyExamples &&
                                        Array.isArray(mainBodyExamples) &&
                                        mainBodyExamples[idx] &&
                                        mainBodyExamples[idx].length > 0
                                      }
                                    />
                                    <Form.Control.Feedback type="invalid">
                                      {fieldErrors.mainBodyExamples}
                                    </Form.Control.Feedback>
                                  </td>

                                  <td>
                                    <Button
                                      className="mt-2"
                                      variant="danger"
                                      size="sm"
                                      onClick={() =>
                                        handleRemoveMainBodyVariable(idx)
                                      }
                                    >
                                      <i className="fa-solid fa-xmark"></i>
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </Col>
                    </Row>

                    {Array.isArray(cards) &&
                      cards.map((card, idx) => (
                        <Card className="mt-2 mb-3" key={idx}>
                          <Card.Body>
                            <h5 className="mb-3">Card {idx + 1}</h5>{" "}
                            {/* <- Add heading here */}
                            <Row className="mb-3">
                              {/* Header Type and Upload side by side */}
                              <Col lg={6}>
                                <Form.Group>
                                  <Form.Label>Header Type</Form.Label>
                                  <Form.Select
                                    value={card.header_type}
                                    onChange={(e) =>
                                      handleCardChange(
                                        idx,
                                        "header_type",
                                        e.target.value,
                                      )
                                    }
                                    isInvalid={
                                      !!fieldErrors[`card_header_type_${idx}`]
                                    }
                                    isValid={
                                      !fieldErrors[`card_header_type_${idx}`] &&
                                      card.header_type
                                    }
                                    required
                                  >
                                    <option value="">Select Header Type</option>
                                    <option value="image">Image</option>
                                    <option value="video">Video</option>
                                  </Form.Select>
                                  <Form.Control.Feedback type="invalid">
                                    {fieldErrors[`card_header_type_${idx}`]}
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>
                              {/* 
  <Col lg={6}>
    <Form.Group>
      <Form.Label>
        Upload {card.header_type === "image" ? "Image" : "Video"}
      </Form.Label>
      <Form.Control
        type="file"
        accept={card.header_type === "image" ? "image/*" : "video/mp4"}
        onChange={(e) => handleCardFileUpload(idx, e.target.files[0])}
        isInvalid={!!fieldErrors[`card_header_file_${idx}`]}
        isValid={
          !fieldErrors[`card_header_file_${idx}`] &&
          (card.header_file || card.header_handle)
        }
        required
      />
      {card.preview_url && card.header_type === "image" && (
        <Image
          src={card.preview_url}
          thumbnail
          style={{ width: "100%", marginTop: 8 }}
        />
      )}
      {card.preview_url && card.header_type === "video" && (
        <video controls style={{ width: "100%", marginTop: 8 }}>
          <source src={card.preview_url} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      )}
      <Form.Control.Feedback type="invalid">
        {fieldErrors[`card_header_file_${idx}`]}
      </Form.Control.Feedback>
    </Form.Group>
  </Col> */}
                            </Row>
                            <Row className="mb-3">
                              {/* Full-width Card Body */}
                              <Col lg={12}>
                                <Form.Group>
                                  <Form.Label>Card Body</Form.Label>
                                  <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={card.body_text}
                                    onChange={(e) =>
                                      handleCardChange(
                                        idx,
                                        "body_text",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Card body text (max 160 chars, supports variables)"
                                    maxLength={160}
                                    isInvalid={
                                      !!fieldErrors[`card_body_text_${idx}`]
                                    }
                                    isValid={
                                      !fieldErrors[`card_body_text_${idx}`] &&
                                      card.body_text.length > 0 &&
                                      card.body_text.length <= 160
                                    }
                                    required
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    {fieldErrors[`card_body_text_${idx}`]}
                                  </Form.Control.Feedback>
                                  <Col className="text-end">
                                    <Button
                                      className="mt-2"
                                      size="sm"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        addCardBodyVariable(idx);
                                      }}
                                    >
                                      Add Variable
                                    </Button>
                                  </Col>
                                </Form.Group>
                              </Col>
                            </Row>
                            {((card.body_text || "").match(VAR_REGEX) || [])
                              .length > 0 && (
                              <Row className="mb-3">
                                <Col lg={12}>
                                  <Table>
                                    <thead>
                                      <tr>
                                        <th>Variable</th>
                                        <th>Example</th>
                                        <th>Action</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(
                                        (card.body_text || "").match(
                                          VAR_REGEX,
                                        ) || []
                                      ).map((token, exIdx) => (
                                        <tr key={exIdx}>
                                          <td>{token}</td>
                                          <td>
                                            <Form.Control
                                              type="text"
                                              style={{ height: "36px" }}
                                              value={
                                                Array.isArray(
                                                  card.body_text_example,
                                                )
                                                  ? card.body_text_example[
                                                      exIdx
                                                    ] || ""
                                                  : ""
                                              }
                                              onChange={(e) =>
                                                handleCardBodyExampleChange(
                                                  idx,
                                                  exIdx,
                                                  e.target.value,
                                                )
                                              }
                                              placeholder={`Example for ${token}`}
                                              isInvalid={
                                                !!fieldErrors[
                                                  `card_body_text_example_${idx}`
                                                ]
                                              }
                                              isValid={
                                                !fieldErrors[
                                                  `card_body_text_example_${idx}`
                                                ] &&
                                                Array.isArray(
                                                  card.body_text_example,
                                                ) &&
                                                card.body_text_example[exIdx] &&
                                                card.body_text_example[exIdx]
                                                  .length > 0 &&
                                                card.body_text_example[exIdx]
                                                  .length <= 2000
                                              }
                                            />
                                            <Form.Control.Feedback type="invalid">
                                              {
                                                fieldErrors[
                                                  `card_body_text_example_${idx}`
                                                ]
                                              }
                                            </Form.Control.Feedback>
                                          </td>
                                          <td>
                                            <Button
                                              className="mt-2"
                                              variant="danger"
                                              size="sm"
                                              onClick={() =>
                                                handleRemoveCardBodyVariable(
                                                  idx,
                                                  exIdx,
                                                )
                                              }
                                            >
                                              <i className="fa-solid fa-xmark"></i>
                                            </Button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                </Col>
                              </Row>
                            )}
                            <Row className="mt-2">
                              <Col>
                                <h6>
                                  Buttons{" "}
                                  <span style={{ color: "red" }}>*</span>
                                </h6>
                                {Array.isArray(card.buttons) &&
                                  card.buttons.map((btn, btnIdx) => (
                                    <Row
                                      key={btnIdx}
                                      className="align-items-center mb-2"
                                    >
                                      <Col lg={3}>
                                        <Form.Select
                                          style={{ height: "36px" }}
                                          value={btn.type}
                                          onChange={(e) =>
                                            handleCardButtonChange(
                                              idx,
                                              btnIdx,
                                              "type",
                                              e.target.value,
                                            )
                                          }
                                          isInvalid={
                                            !!fieldErrors[`card_buttons_${idx}`]
                                          }
                                          isValid={
                                            !fieldErrors[
                                              `card_buttons_${idx}`
                                            ] &&
                                            (card.buttons || []).length > 0 &&
                                            (card.buttons || []).length <= 2
                                          }
                                          required
                                        >
                                          <option value="">
                                            Select Button Type
                                          </option>
                                          <option value="quick_reply">
                                            Quick Reply
                                          </option>
                                          <option value="url">URL</option>
                                          <option value="phone_number">
                                            Phone Number
                                          </option>
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                          {fieldErrors[`card_buttons_${idx}`]}
                                        </Form.Control.Feedback>
                                        <Form.Text className="text-muted">
                                          Required. Max 2.
                                        </Form.Text>
                                      </Col>
                                      <Col lg={3}>
                                        <Form.Control
                                          style={{ height: "36px" }}
                                          type="text"
                                          value={btn.text}
                                          onChange={(e) =>
                                            handleCardButtonChange(
                                              idx,
                                              btnIdx,
                                              "text",
                                              e.target.value,
                                            )
                                          }
                                          placeholder="Button Text"
                                          isInvalid={
                                            !!fieldErrors[
                                              `card_btn_text_${idx}_${btnIdx}`
                                            ]
                                          }
                                          isValid={
                                            !fieldErrors[
                                              `card_btn_text_${idx}_${btnIdx}`
                                            ] &&
                                            (btn.text || "").length > 0 &&
                                            (btn.text || "").length <= 25
                                          }
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          {
                                            fieldErrors[
                                              `card_btn_text_${idx}_${btnIdx}`
                                            ]
                                          }
                                        </Form.Control.Feedback>
                                        <Form.Text className="text-muted">
                                          Required. Max 25.
                                        </Form.Text>
                                      </Col>
                                      {btn.type === "url" && (
                                        <Col lg={4}>
                                          <Form.Control
                                            style={{ height: "36px" }}
                                            type="text"
                                            value={btn.url || ""}
                                            onChange={(e) =>
                                              handleCardButtonChange(
                                                idx,
                                                btnIdx,
                                                "url",
                                                e.target.value,
                                              )
                                            }
                                            placeholder="URL"
                                            isInvalid={
                                              !!fieldErrors[
                                                `card_btn_url_${idx}_${btnIdx}`
                                              ]
                                            }
                                            isValid={
                                              !fieldErrors[
                                                `card_btn_url_${idx}_${btnIdx}`
                                              ] &&
                                              (btn.url || "").length > 0 &&
                                              (btn.url || "").length <= 2000
                                            }
                                          />
                                          <Form.Control.Feedback type="invalid">
                                            {
                                              fieldErrors[
                                                `card_btn_url_${idx}_${btnIdx}`
                                              ]
                                            }
                                          </Form.Control.Feedback>
                                          <Form.Text className="text-muted">
                                            Required.
                                          </Form.Text>
                                        </Col>
                                      )}
                                      {btn.type === "phone_number" && (
                                        <Col lg={4}>
                                          <Form.Control
                                            style={{ height: "36px" }}
                                            type="text"
                                            value={btn.phone_number || ""}
                                            onChange={(e) =>
                                              handleCardButtonChange(
                                                idx,
                                                btnIdx,
                                                "phone_number",
                                                e.target.value,
                                              )
                                            }
                                            placeholder="Phone Number"
                                            isInvalid={
                                              !!fieldErrors[
                                                `card_btn_phone_${idx}_${btnIdx}`
                                              ]
                                            }
                                            isValid={
                                              !fieldErrors[
                                                `card_btn_phone_${idx}_${btnIdx}`
                                              ] &&
                                              (btn.phone_number || "").length >
                                                0 &&
                                              (btn.phone_number || "").length <=
                                                20
                                            }
                                          />
                                          <Form.Control.Feedback type="invalid">
                                            {
                                              fieldErrors[
                                                `card_btn_phone_${idx}_${btnIdx}`
                                              ]
                                            }
                                          </Form.Control.Feedback>
                                          <Form.Text className="text-muted">
                                            Required. Max 20.
                                          </Form.Text>
                                        </Col>
                                      )}
                                      <Col lg={2}>
                                        <Button
                                          className="mb-4"
                                          variant="danger"
                                          onClick={() =>
                                            removeCardButton(idx, btnIdx)
                                          }
                                        >
                                          <i className="fa-solid fa-xmark"></i>
                                        </Button>
                                      </Col>
                                    </Row>
                                  ))}
                                {Array.isArray(card.buttons) &&
                                  (card.buttons || []).length < 2 && (
                                    <Button
                                      size="sm"
                                      onClick={() => addCardButton(idx)}
                                    >
                                      Add Button
                                    </Button>
                                  )}
                              </Col>
                            </Row>
                            <Row>
                              <Col className="text-end">
                                {cards.length > 1 && (
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => removeCard(idx)}
                                  >
                                    Remove Card
                                  </Button>
                                )}
                              </Col>
                            </Row>
                          </Card.Body>
                        </Card>
                      ))}
                    <Button
                      className="mb-3"
                      onClick={addCard}
                      disabled={
                        Array.isArray(cards) ? cards.length >= 10 : false
                      }
                    >
                      Add Card
                    </Button>
                    <Row>
                      <Col className="text-end">
                        <Button
                          variant="primary"
                          type="submit"
                          disabled={isSending}
                        >
                          {isSending
                            ? isEditMode
                              ? "Updating..."
                              : "Saving..."
                            : isEditMode
                              ? "Update Carousel Template"
                              : "Save Carousel Template"}
                        </Button>
                      </Col>
                    </Row>
                    {error && <div className="text-danger mt-2">{error}</div>}
                    {/* {payload && (
                  <pre
                    className="mt-3"
                    style={{
                      background: "#f8f9fa",
                      padding: 16,
                      borderRadius: 8,
                    }}
                  >
                    {JSON.stringify(payload, null, 2)}
                  </pre>
                )} */}
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <ToastContainer />
        </Container>
      ) : (
        <Container className="mt-1">
          <Row className="mx-5">
            <Col lg={12} sm={12} xs={12}>
              <Card className="h-100" style={{ border: "none" }}>
                <Card.Body>
                  <Row className="mb-3">
                    <Col lg={12} sm={12} xs={12}>
                      <div className="sk-cube-grid">
                        <div className="sk-cube sk-cube1"></div>
                        <div className="sk-cube sk-cube2"></div>
                        <div className="sk-cube sk-cube3"></div>
                        <div className="sk-cube sk-cube4"></div>
                        <div className="sk-cube sk-cube5"></div>
                        <div className="sk-cube sk-cube6"></div>
                        <div className="sk-cube sk-cube7"></div>
                        <div className="sk-cube sk-cube8"></div>
                        <div className="sk-cube sk-cube9"></div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      )}
    </>
  );
};

export default CarouselTemplate;
